import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import './Returns.css';
import api from '../../api';
import Select from 'react-select';
import { FaTrash } from 'react-icons/fa';

const AddReturn = () => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [items, setItems] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [itemStockMap, setItemStockMap] = useState({});
  const { showPopup } = usePopup();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [formData, setFormData] = useState({
    return_date: new Date().toLocaleDateString('en-CA'),
    department_id: '',
    received_by: '',
    remarks: '',
    items: [{ item_id: '', quantity: 1, unit: '', reason: 'Excess' }]
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);

    const fetchBasics = async () => {
      try {
        const [deptRes, itemsRes] = await Promise.all([
          api.get('/departments'),
          api.get('/items') // මෙය දැන් calculated stock එක return කරයි
        ]);
        setDepartments(deptRes.data || []);
        setItems(itemsRes.data || []);
        
        // Stock data එක itemsRes.data එකෙන්ම ගන්න
        const stockMap = {};
        itemsRes.data.forEach(item => {
          stockMap[item.id] = item.current_stock || 0;
        });
        setItemStockMap(stockMap);
      } catch (error) {
        console.error('Error fetching basics:', error);
        showPopup('error', 'Failed to load departments or items');
      }
    };

    fetchBasics();
  }, [showPopup]);

  const fetchAllItemStocks = async (itemsList) => {
    try {
      const stockMap = {};
      for (const item of itemsList) {
        const response = await api.get(`/items/${item.id}`);
        stockMap[item.id] = response.data.current_stock || 0;
      }
      setItemStockMap(stockMap);
    } catch (error) {
      console.error('Error fetching item stocks:', error);
    }
  };

  const fetchItemStock = async (itemId) => {
    try {
      const response = await api.get(`/items/${itemId}`);
      const stock = response.data.current_stock || 0;
      setItemStockMap(prev => ({
        ...prev,
        [itemId]: stock
      }));
      return stock;
    } catch (error) {
      console.error('Error fetching item stock:', error);
      return 0;
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    
    if (field === 'quantity') {
      value = value === '' || value === null ? '' : Number(value);
      if (isNaN(value)) value = 0;
    }

    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'item_id') {
      const selectedItem = items.find(it => String(it.id) === String(value));
      newItems[index].unit = selectedItem ? selectedItem.unit : '';
      
      if (value) {
        fetchItemStock(value);
      }
    }

    setFormData(prev => ({ ...prev, items: newItems }));

    const isLast = index === formData.items.length - 1;
    if (isLast && (field === 'item_id' || field === 'quantity') && value) {
      const hasValue = newItems[index].item_id || (Number(newItems[index].quantity) > 0);
      if (hasValue) addItemRow();
    }
  };

  const addItemRow = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_id: '', quantity: 1, unit: '', reason: 'Excess' }]
    }));
  };

  const removeItemRow = (index) => {
    if (formData.items.length <= 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // A second click while the first POST is in flight must not record the return twice
    if (submittingRef.current) return;

    if (!currentUser || !currentUser.id) {
      showPopup('error', 'User information is missing. Please login again.');
      navigate('/login');
      return;
    }

    const validItems = formData.items.filter(item => 
      item.item_id && Number(item.quantity) > 0
    );

    if (validItems.length === 0) {
      showPopup('error', 'Please add at least one item with quantity');
      return;
    }

    const payload = {
      ...formData,
      items: validItems
    };

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const response = await api.post('/returns', payload);
      
      // After successful return, refresh all stock values
      await fetchAllItemStocks(items);
      
      showPopup('success', `Return recorded successfully! Reference: ${response.data.return_number}`);
      navigate('/returns/history');
    } catch (error) {
      console.error('Error creating return:', error);
      showPopup('error', 'Failed to record return');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const itemOptions = items.map(itm => ({
    value: itm.id,
    label: `${itm.name} (${itm.item_code || ''}) - ${itm.unit}`,
    unit: itm.unit
  }));

  const selectStyles = {
    menuPortal: base => ({ ...base, zIndex: 'var(--z-popover)' })
  };

  return (
    <div className="returns-container">
      <div className="page-header">
        <h1>Department Return</h1>
      </div>

      <form onSubmit={handleSubmit} className="purchase-form">
        <div className="form-row dept-return-fields">
          <div className="form-group">
            <label>Return Date</label>
            <input
              type="date"
              value={formData.return_date}
              onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Department from</label>
            <select
              value={formData.department_id}
              onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
              required
            >
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Received By</label>
            <input
              type="text"
              value={formData.received_by}
              onChange={(e) => setFormData({ ...formData, received_by: e.target.value })}
              placeholder="Name of person receiving the return"
              required
            />
          </div>

          <div className="form-group full-width">
            <label>Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Additional notes about the return..."
              rows="3"
            />
          </div>
        </div>

        <h3>Returned Items</h3>
        {/* Scrolls sideways on narrow screens instead of being cut off */}
        <div style={{ overflowX: 'auto' }}>
          <table className="item-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Current Stock</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Reason for Return</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item, index) => (
                <tr key={index}>
                  <td style={{ minWidth: 300 }}>
                    <Select
                      options={itemOptions}
                      value={itemOptions.find(opt => String(opt.value) === String(item.item_id)) || null}
                      onChange={(selected) => handleItemChange(index, 'item_id', selected ? selected.value : '')}
                      isSearchable
                      placeholder="Search or select item..."
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </td>
                  <td>
                    <span className="current-stock-display">
                      {item.item_id ? (itemStockMap[item.item_id] || 0) : '-'}
                    </span>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.unit}
                      readOnly
                      className="unit-display"
                    />
                  </td>
                  <td>
                    <select
                      value={item.reason}
                      onChange={(e) => handleItemChange(index, 'reason', e.target.value)}
                      required
                    >
                      <option value="Excess">Excess</option>
                      <option value="Damaged">Damaged</option>
                      <option value="Wrong Issue">Wrong Issue</option>
                      <option value="Expired">Expired</option>
                      <option value="Other">Other</option>
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeItemRow(index)}
                      disabled={formData.items.length <= 1}
                      title="Remove item"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className="btn-add" onClick={addItemRow}>
          + Add Item
        </button>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Return'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddReturn;